import GeneralTracker from '../GeneralTracker';


const VISIBILITY_THRESHOLD = 0.4;
const PAGE_PRESENCE_THRESHOLD = 1000;
const RENDER_CHECK_DELAY = 1000;

export default class SegmentedDirectoryTracker extends GeneralTracker {
  STORAGE_KEY = 'trackingSegmentedDirectoryKey';
  CAPTURE_NAME = 'ui:directory:segmented:usage';
  ENTITY = 'Segments';
  VERSION = '1';
  SEGMENTS = {};
  UNHANDLED_SEGMENTS = [];
  MODE = 'segmented';

  init(service) {
    super.init(service);
    this.currentSegments = {};
    this.visibilityTransition = {};

    this.UNHANDLED_SEGMENTS = [];
    this.SEGMENTS = {};

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const { target, isIntersecting: currentVisibilityState } = entry;

        if (this.MODE !== 'segmented') {
          if (!currentVisibilityState) {
            return;
          }

          this.MODE = 'segmented';
        }

        const parent = target.parentNode;
        const dataID = parent?.dataset?.id;

        const keyIndex = this.UNHANDLED_SEGMENTS.indexOf(dataID);
        if (keyIndex > -1) {
          this.currentSegments[dataID] = {
            ...this.SEGMENTS[dataID],
            inView: currentVisibilityState,
          };

          this.UNHANDLED_SEGMENTS.splice(keyIndex, 1);

          return;
        }

        if (dataID in this.visibilityTransition) {
          const { transitionTo, timeout } = this.visibilityTransition[dataID];

          if (transitionTo !== currentVisibilityState) {
            clearTimeout(timeout);
          }

          delete this.visibilityTransition[dataID];

          return;
        }

        const timeout = setTimeout(() => {
          this.currentSegments[dataID].inView = currentVisibilityState;
          const event = currentVisibilityState ? 'segmentIsInView' : 'segmentIsOutOfView';
          service.emit(event, { segment: dataID });

          delete this.visibilityTransition[dataID];
        }, PAGE_PRESENCE_THRESHOLD);

        this.visibilityTransition[dataID] = {
          transitionTo: currentVisibilityState,
          timeout,
        };
      });
    }, {
      root: null,
      rootMargin: '0px',
      threshold: VISIBILITY_THRESHOLD,
    });
  }

  setHandlers() {
    const service = this.getService();

    service.on('segmentedDirectoryInit', this.onSegmentedDirectoryInit);
    service.on('segmentedDirectoryRendered', this.onSegmentedDirectoryRendered);
    service.on('showAllFromSegment', this.onShowAll);
    service.on('backButtonClicked', this.onBackButtonClicked);
    service.on('useNextOnSegment', this.onUseNext);
    service.on('usePrevOnSegment', this.onUsePrev);
    service.on('segmentIsShown', this.onSegmentIsShown);
    service.on('segmentIsInView', this.onSegmentIsInView);
    service.on('segmentIsOutOfView', this.onSegmentIsOutOfView);

    service.on('segmentHideOptionUsed', this.onSegmentHideOptionUsed);
  }

  onSegmentedDirectoryInit = (payload) => {
    const { ref } = payload;

    if (ref === null) {
      return;
    }

    let timeout = null;

    const mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        const { addedNodes } = mutation;

        addedNodes?.forEach?.((node) => {
          const key = node?.dataset?.id;

          if (key === undefined) {
            return;
          }

          this.SEGMENTS[key] = { key };
          this.UNHANDLED_SEGMENTS.push(key);

          if (timeout !== null) {
            clearTimeout(timeout);
          }

          timeout = setTimeout(() => {
            const service = this.getService();

            service.emit('segmentedDirectoryRendered', this.currentSegments);
            mutationObserver.disconnect();
          }, RENDER_CHECK_DELAY);
        });
      });
    });

    mutationObserver.observe(ref, { childList: true });
  };

  onSegmentedDirectoryRendered = (payload) => {
    this.track({
      action: 'renderAll',
      details: { segments: payload },
    });
  };

  onBackButtonClicked = (payload) => {
    this.UNHANDLED_SEGMENTS = Object.keys(this.SEGMENTS);
    this.MODE = 'segmented';

    this.track({
      action: 'backButtonClicked',
      details: {
        path: payload.page.pathname,
        search: payload.page.search,
      },
    });
  };

  onShowAll = (payload) => {
    this.UNHANDLED_SEGMENTS = Object.keys(this.SEGMENTS);
    this.MODE = 'showAll';

    this.track({
      action: 'showAll',
      details: payload,
    });
  };

  onUseNext = (payload) => {
    this.track({
      action: 'useNext',
      details: payload,
    });
  };

  onUsePrev = (payload) => {
    this.track({
      action: 'usePrev',
      details: payload,
    });
  };

  onSegmentHideOptionUsed = (payload) => {
    this.track({
      action: 'hideOptionUsed',
      details: payload,
    });
  };

  onSegmentIsShown = (payload) => {
    const { container } = payload;

    if (!container) {
      return;
    }

    const segmentCarousel = container.querySelector('[data-name=Carousel]');

    this.observer.observe(segmentCarousel);
  };

  onSegmentIsInView = (payload) => {
    this.track({
      action: 'inView',
      details: payload,
    });
  };

  onSegmentIsOutOfView = (payload) => {
    this.track({
      action: 'outOfView',
      details: payload,
    });
  };

  parsePayload = (payload) => {
    const { action, details: payloadDetails, ...payloadOther } = payload;

    const trackPayload = {
      action: `${action}${this.ENTITY}`,
      labels: {
        ...payloadDetails,
        other: payloadOther,
      },
    };

    return trackPayload;
  };

  track = (payload) => {
    const trackPayload = this.parsePayload(payload);

    super.track(trackPayload);
  };
}
